import { ActivityLog, Applicant } from 'types/graphql'

import ActivityTimestamp from 'src/components/ActivityLogs/shared/ActivityTimestamp'
import ActorBadge from 'src/components/ActivityLogs/shared/ActorBadge'
import { useReadOnlyEmailModalContext } from 'src/context/ReadOnlyEmailModalContext'
import { ActivityLogActorType } from 'src/graphql/types/activityLogs'
import { EmailDirection } from 'src/graphql/types/emails'
import { buildFullName } from 'src/lib/formatters'
import { decodeHtmlEntities } from 'src/lib/string-util'

interface EmailLogCardProps {
  activityLog: ActivityLog
  applicant?: Applicant
}

// Every email activity-log writer hardcodes content as "Subject: <subject>";
// strip that prefix so the bubble shows just the subject line. Subject-less
// emails produce "" or the literal "undefined" (the Nylas writer doesn't
// guard its template literal) — show the standard "(No subject)" instead.
// Subjects/snippets arrive HTML-encoded from the provider, so decode entities
// like the old EmailCard list did.
const getSubject = (content?: string | null) => {
  const subject = content
    ? decodeHtmlEntities(content.replace(/^Subject:\s*/i, '')).trim()
    : ''
  return subject && subject !== 'undefined' ? subject : '(No subject)'
}

// Sidebar email bubble — the email-thread counterpart to SMSLog/MiniCallLog.
// Outbound emails sit right, inbound left; the header reads like the SMS
// thread (sender name or an "Auto" badge + timestamp) and the bubble shows the
// subject line plus a two-line snippet. Clicking opens the read-only email
// modal on this bubble's email, same as clicking an EMAIL row in the Activity
// Log tab.
const EmailLog = ({ activityLog, applicant }: EmailLogCardProps) => {
  const readOnlyEmailModal = useReadOnlyEmailModalContext()

  // Prefer the Email record's direction; rows fetched without the email
  // relation fall back to the title every writer hardcodes
  // ('Email Sent' | 'Email Received').
  const isOutbound = activityLog.email?.direction
    ? activityLog.email.direction === EmailDirection.OUTBOUND
    : activityLog.title !== 'Email Received'

  const isAutomated =
    activityLog.actorType === ActivityLogActorType.AUTOMATED_RULE ||
    activityLog.actorType === ActivityLogActorType.SYSTEM
  const isBulk = activityLog.actorType === ActivityLogActorType.BULK_ACTION

  // Who's on this bubble's side: outbound → the recruiter who sent it;
  // inbound → the applicant. (On inbound rows the log's employee is whose
  // inbox received the email, not the sender — the sender is the applicant.)
  const displayName = isOutbound
    ? buildFullName(activityLog.employee)
    : applicant
    ? buildFullName(applicant)
    : ''

  const subject = getSubject(activityLog.content)
  const snippet = activityLog.email?.snippet
    ? decodeHtmlEntities(activityLog.email.snippet)
    : null

  // Prefer the email's provider date — a delayed/replayed Nylas webhook makes
  // createdAt the sync time, not when the email was actually sent/received.
  const timestamp = activityLog.email?.date ?? activityLog.createdAt

  // Click-through mirrors ActivityLogTab's EMAIL row click: open the
  // read-only email modal on this bubble's own email, located within its
  // thread. Rows without a thread (or rendered outside the dashboard's
  // ReadOnlyEmailModalContextProvider) stay inert.
  const emailThreadId = activityLog.email?.emailThreadId
  const canOpen = !!readOnlyEmailModal?.handleOpen && !!emailThreadId
  const openEmail = () => {
    if (canOpen) {
      readOnlyEmailModal.handleOpen({
        emailThreadId,
        selectedEmailId: activityLog.emailId,
      })
    }
  }

  const interactiveProps = canOpen
    ? {
        role: 'button',
        tabIndex: 0,
        'aria-label': `Open email${subject ? ` — ${subject}` : ''}`,
        onClick: openEmail,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            openEmail()
          }
        },
      }
    : {}

  return (
    <div
      className={`flex max-w-[75%] flex-col gap-1 ${
        isOutbound ? 'ml-auto' : 'mr-auto'
      }`}
    >
      {/* Name pinned left, timestamp pinned right — uniform across SMS/Call/
          Email threads regardless of which side the bubble sits on. */}
      <div className="flex w-full flex-row items-center justify-between gap-2 text-xs text-doubleNickel-gray-600">
        {isAutomated ? (
          <ActorBadge variant="auto" label="Sent automatically by a rule" />
        ) : (
          <div className="flex items-center gap-1.5">
            {displayName && <span className="font-medium">{displayName}</span>}
            {isBulk && (
              <ActorBadge variant="bulk" label="Sent as part of a bulk email" />
            )}
          </div>
        )}
        <ActivityTimestamp date={timestamp} />
      </div>

      <div
        {...interactiveProps}
        className={`flex flex-col gap-1 rounded-t-lg p-3 transition-colors ${
          isOutbound
            ? 'rounded-bl-xl bg-doubleNickel-brand-25'
            : 'rounded-br-xl bg-doubleNickel-gray-50'
        } ${
          canOpen
            ? `cursor-pointer ${
                isOutbound
                  ? 'hover:bg-doubleNickel-brand-50'
                  : 'hover:bg-doubleNickel-gray-100'
              }`
            : ''
        }`}
      >
        <span className="min-w-0 text-sm font-medium text-doubleNickel-gray-900 [overflow-wrap:anywhere]">
          {subject}
        </span>
        {snippet && (
          <div className="line-clamp-2 text-xs text-doubleNickel-gray-500 [overflow-wrap:anywhere]">
            {snippet}
          </div>
        )}
      </div>
    </div>
  )
}

export default EmailLog
